// Authentication utility functions for handling token refresh and API requests
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';

/**
 * Get a fresh authentication token, with automatic refresh if needed
 * @returns {Promise<string|null>} The authentication token or null if unavailable
 */
export const getAuthToken = async (forceRefresh = false) => {
  try {
    const session = await fetchAuthSession({ forceRefresh });
    const token = session?.tokens?.idToken?.toString();
    
    if (!token) {
      console.error('No authentication token available');
      return null;
    }
    
    return token;
  } catch (error) {
    console.error('Error getting authentication token:', error);
    return null;
  }
};

/**
 * Make an authenticated API request with automatic token refresh on 401 errors
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Axios request options
 * @param {boolean} retryOn401 - Whether to retry with refreshed token on 401
 * @returns {Promise} Axios response
 */
export const makeAuthenticatedRequest = async (url, options = {}, retryOn401 = true) => {
  try {
    const token = await getAuthToken();
    
    if (!token) {
      throw new Error('No authentication token available');
    }

    const requestOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    };

    const response = await axios(url, requestOptions);
    return response;
  } catch (error) {
    // If it's a 401 error and we haven't retried yet, try with a refreshed token
    if (error.response?.status === 401 && retryOn401) {
      try {
        const refreshedToken = await getAuthToken(true); // Force refresh
        
        if (refreshedToken) {
          const retryOptions = {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${refreshedToken}`,
              ...options.headers
            }
          };

          const retryResponse = await axios(url, retryOptions);
          return retryResponse;
        }
      } catch (retryError) {
        console.error('Retry with refreshed token failed:', retryError);
        throw retryError;
      }
    }
    
    throw error;
  }
};

/**
 * Check if the current user is authenticated
 * @returns {Promise<boolean>} True if authenticated, false otherwise
 */
export const isAuthenticated = async () => {
  try {
    const token = await getAuthToken();
    return token !== null;
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return false;
  }
};

/**
 * Handle authentication errors with user-friendly messages
 * @param {Error} error - The error object
 * @param {string} operation - Description of the operation that failed
 */
export const handleAuthError = (error, operation = 'operation') => {
  console.error(`Authentication error during ${operation}:`, error);
  
  if (error.response?.status === 401) {
    alert('Your session has expired. Please refresh the page and log in again.');
    // Optionally redirect to login or refresh the page
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  } else if (error.response?.status === 403) {
    alert('You do not have permission to perform this action.');
  } else {
    alert(`Unable to complete ${operation}. Please try again.`);
  }
};
